import { UseQueryResult, useQueries, useQuery } from "@tanstack/react-query";

import {
  ProductDetailParams,
  getProduct,
} from "../../services/productPlaneApi";
import { ProductDetail } from "../../types/productPlane";
import { queryKeys } from "./keys";
import { planeErrorMessage } from "./planeError";

// Sources asked for with a product: the most the backend gives in one page.
// The source table pages them itself, and a source mark finds its row there.
export const PRODUCT_SOURCES_LIMIT = 200;

const PRODUCT_PARAMS: ProductDetailParams = {
  sources_limit: PRODUCT_SOURCES_LIMIT,
};

export interface UseProductResult {
  product: ProductDetail | null;
  loading: boolean;
  // Why the product cannot be shown; null while it can, or while it loads
  error: string | null;
}

/**
 * TanStack Query hook for one product.
 *
 * @param productId - The id of the product; null fetches nothing
 * @returns The product, the loading state and a message for a failed read
 */
export function useProduct(productId: string | null): UseProductResult {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.productPlane.product(productId!, { ...PRODUCT_PARAMS }),
    queryFn: () => getProduct(productId!, PRODUCT_PARAMS),
    enabled: !!productId,
    staleTime: 2 * 60 * 1000,
  });

  return {
    product: data ?? null,
    loading: isLoading,
    error: error ? planeErrorMessage(error, "Product") : null,
  };
}

/**
 * The read `useProduct` makes, for a read outside a render (an export): with
 * `queryClient.fetchQuery` it answers from the cache a product opened before,
 * and what it reads is there for the product's page.
 *
 * @param productId - The id of the product
 * @returns The query's key, function and staleness
 */
export const productQuery = (productId: string) => ({
  queryKey: queryKeys.productPlane.product(productId, { ...PRODUCT_PARAMS }),
  queryFn: () => getProduct(productId, PRODUCT_PARAMS),
  staleTime: 2 * 60 * 1000,
});

// The reads of several products as one list, in the order asked. A function
// of the module, so that the list stays the same until a read changes.
const combineProducts = (
  results: UseQueryResult<ProductDetail>[],
): UseProductResult[] =>
  results.map(({ data, isLoading, error }) => ({
    product: data ?? null,
    loading: isLoading,
    error: error ? planeErrorMessage(error, "Product") : null,
  }));

/**
 * TanStack Query hook for several products, each read on its own.
 *
 * Every read is the read `useProduct` makes, under the same key: a product
 * opened before answers from the cache, and one that fails leaves the
 * others as they are.
 *
 * @param productIds - The ids of the products
 * @returns One result per id, in the order of the ids
 */
export function useProducts(productIds: string[]): UseProductResult[] {
  return useQueries({
    queries: productIds.map((productId) => ({
      queryKey: queryKeys.productPlane.product(productId, {
        ...PRODUCT_PARAMS,
      }),
      queryFn: () => getProduct(productId, PRODUCT_PARAMS),
      staleTime: 2 * 60 * 1000,
    })),
    combine: combineProducts,
  });
}
